import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Cloud,
  Code,
  Palette,
  Layers,
  Database,
  Server,
  Cpu,
  Globe,
  Figma,
  Monitor,
} from 'lucide-react';

interface Skill {
  id: number;
  title: string;
  description: string;
  technologies: Technology[];
  icon: React.ReactNode;
  gradient: string;
}

interface Technology {
  name: string;
  icon: React.ReactNode;
  color: string;
}

const SkillsCardSlider: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const skills: Skill[] = [
    {
      id: 1,
      title: 'AI & Cloud Architect',
      description:
        'Designing scalable cloud infrastructure and implementing AI-driven solutions with modern orchestration and data pipelines.',
      icon: <Cloud className="h-8 w-8" />,
      gradient: 'from-blue-500 to-cyan-500',
      technologies: [
        {
          name: 'AWS',
          icon: <Server className="h-5 w-5" />,
          color: 'text-orange-400',
        },
        {
          name: 'Azure',
          icon: <Cloud className="h-5 w-5" />,
          color: 'text-blue-400',
        },
        {
          name: 'TensorFlow',
          icon: <Cpu className="h-5 w-5" />,
          color: 'text-yellow-400',
        },
        {
          name: 'Docker',
          icon: <Database className="h-5 w-5" />,
          color: 'text-blue-300',
        },
        {
          name: 'Kubernetes',
          icon: <Globe className="h-5 w-5" />,
          color: 'text-purple-400',
        },
        {
          name: 'Python',
          icon: <Code className="h-5 w-5" />,
          color: 'text-green-400',
        },
      ],
    },
    {
      id: 2,
      title: 'Full Stack Developer',
      description:
        'Building robust end-to-end applications with modern frameworks, databases, and scalable backend architectures.',
      icon: <Layers className="h-8 w-8" />,
      gradient: 'from-purple-500 to-pink-500',
      technologies: [
        {
          name: 'React',
          icon: <Code className="h-5 w-5" />,
          color: 'text-cyan-400',
        },
        {
          name: 'Node.js',
          icon: <Server className="h-5 w-5" />,
          color: 'text-green-400',
        },
        {
          name: 'PostgreSQL',
          icon: <Database className="h-5 w-5" />,
          color: 'text-blue-400',
        },
        {
          name: 'MongoDB',
          icon: <Database className="h-5 w-5" />,
          color: 'text-green-500',
        },
        {
          name: 'GraphQL',
          icon: <Globe className="h-5 w-5" />,
          color: 'text-pink-400',
        },
        {
          name: 'TypeScript',
          icon: <Code className="h-5 w-5" />,
          color: 'text-blue-500',
        },
      ],
    },
    {
      id: 3,
      title: 'Frontend Developer',
      description:
        'Creating beautiful, responsive user interfaces with cutting-edge frameworks and modern development tools.',
      icon: <Monitor className="h-8 w-8" />,
      gradient: 'from-green-500 to-teal-500',
      technologies: [
        {
          name: 'Next.js',
          icon: <Globe className="h-5 w-5" />,
          color: 'text-white',
        },
        {
          name: 'React',
          icon: <Code className="h-5 w-5" />,
          color: 'text-cyan-400',
        },
        {
          name: 'Tailwind',
          icon: <Palette className="h-5 w-5" />,
          color: 'text-teal-400',
        },
        {
          name: 'Framer Motion',
          icon: <Layers className="h-5 w-5" />,
          color: 'text-purple-400',
        },
        {
          name: 'Vite',
          icon: <Cpu className="h-5 w-5" />,
          color: 'text-yellow-400',
        },
        {
          name: 'Figma',
          icon: <Figma className="h-5 w-5" />,
          color: 'text-red-400',
        },
      ],
    },
    {
      id: 4,
      title: 'Product Designer',
      description:
        'Designing intuitive user experiences with comprehensive research, prototyping, and collaborative design systems.',
      icon: <Palette className="h-8 w-8" />,
      gradient: 'from-orange-500 to-red-500',
      technologies: [
        {
          name: 'Figma',
          icon: <Figma className="h-5 w-5" />,
          color: 'text-red-400',
        },
        {
          name: 'Adobe XD',
          icon: <Palette className="h-5 w-5" />,
          color: 'text-purple-400',
        },
        {
          name: 'Sketch',
          icon: <Monitor className="h-5 w-5" />,
          color: 'text-yellow-400',
        },
        {
          name: 'Principle',
          icon: <Layers className="h-5 w-5" />,
          color: 'text-blue-400',
        },
        {
          name: 'InVision',
          icon: <Globe className="h-5 w-5" />,
          color: 'text-pink-400',
        },
        {
          name: 'Miro',
          icon: <Code className="h-5 w-5" />,
          color: 'text-orange-400',
        },
      ],
    },
  ];

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % skills.length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + skills.length) % skills.length);
  };

  const getCardPosition = (index: number) => {
    const diff = index - currentIndex;
    if (diff === 0) return 'translate-x-0 scale-100 z-30 opacity-100';
    if (diff === 1 || diff === -(skills.length - 1))
      return 'translate-x-16 sm:translate-x-32 lg:translate-x-80 scale-90 sm:scale-80 lg:scale-75 z-20 opacity-40 sm:opacity-50 lg:opacity-60';
    if (diff === -1 || diff === skills.length - 1)
      return '-translate-x-16 sm:-translate-x-32 lg:-translate-x-80 scale-90 sm:scale-80 lg:scale-75 z-20 opacity-40 sm:opacity-50 lg:opacity-60';
    return 'translate-x-32 sm:translate-x-48 lg:translate-x-96 scale-75 sm:scale-60 lg:scale-50 z-10 opacity-0';
  };

  return (
    <motion.div
      className="mx-auto w-full max-w-6xl px-4 py-8 sm:py-12 lg:py-16"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <motion.div
        className="relative flex h-88 items-center justify-center overflow-hidden sm:h-96 lg:h-[28rem]"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        {/* Cards Container */}
        <div className="relative flex h-full w-full items-center justify-center">
          {skills.map((skill, index) => (
            <motion.div
              key={skill.id}
              className={`absolute h-80 w-72 transform transition-all duration-700 ease-in-out sm:h-88 sm:w-80 lg:h-96 lg:w-80 ${getCardPosition(index)}`}
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.6 + index * 0.1 }}
            >
              <div className="relative h-full w-full rounded-xl border border-slate-700/50 bg-gradient-to-br from-slate-900/80 to-slate-800/80 p-4 shadow-2xl backdrop-blur-sm sm:rounded-2xl sm:p-6">
                {/* Gradient Border Effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${skill.gradient} rounded-2xl opacity-20 blur-sm`}
                ></div>
                <div
                  className={`absolute inset-[1px] bg-gradient-to-r ${skill.gradient} rounded-2xl opacity-10`}
                ></div>

                {/* Card Content */}
                <div className="relative z-10 flex h-full flex-col">
                  {/* Header */}
                  <div className="mb-2 flex items-center gap-2 sm:mb-3 sm:gap-3">
                    <div
                      className={`bg-gradient-to-r p-1.5 sm:p-2 ${skill.gradient} rounded-lg text-white`}
                    >
                      <div className="h-6 w-6 sm:h-8 sm:w-8">{skill.icon}</div>
                    </div>
                    <div className="text-lg leading-tight font-bold text-white sm:text-xl">
                      {skill.title}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="mb-3 flex-grow text-xs leading-relaxed text-gray-300 sm:mb-4 sm:text-sm">
                    {skill.description}
                  </p>

                  {/* Technologies Grid */}
                  <div className="space-y-1.5 sm:space-y-2">
                    <h4 className="text-xs font-semibold tracking-wider text-gray-400 uppercase sm:text-sm">
                      Technologies
                    </h4>
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                      {skill.technologies.map((tech, techIndex) => (
                        <div
                          key={techIndex}
                          className="flex flex-col items-center gap-1 rounded-lg border border-slate-600/30 bg-slate-800/50 p-1 transition-colors hover:border-slate-500/50 sm:p-1.5"
                        >
                          <div className={tech.color}>
                            <div className="h-4 w-4 sm:h-5 sm:w-5">
                              {tech.icon}
                            </div>
                          </div>
                          <span className="text-center text-[10px] leading-tight font-medium text-gray-400 sm:text-xs">
                            {tech.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <motion.button
          onClick={prevSlide}
          aria-label="View previous skill card"
          className="absolute left-2 z-40 cursor-pointer rounded-full border border-slate-600/50 bg-slate-800/80 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:border-blue-500/50 hover:bg-slate-700/80 hover:text-blue-400 sm:left-4 sm:p-3"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
        </motion.button>

        <motion.button
          onClick={nextSlide}
          aria-label="View next skill card"
          className="absolute right-2 z-40 cursor-pointer rounded-full border border-slate-600/50 bg-slate-800/80 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:border-blue-500/50 hover:bg-slate-700/80 hover:text-blue-400 sm:right-4 sm:p-3"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
        </motion.button>
      </motion.div>

      {/* Dots Indicator */}
      <motion.div
        className="mt-6 flex justify-center gap-2 sm:mt-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        {skills.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to skill card ${index + 1} of ${skills.length}`}
            aria-current={index === currentIndex ? 'true' : 'false'}
            className={`h-2 w-2 rounded-full transition-all duration-300 sm:h-3 sm:w-3 ${
              index === currentIndex
                ? 'scale-110 bg-green-500'
                : 'bg-slate-600 hover:bg-slate-500'
            }`}
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4, delay: 1.4 + index * 0.1 }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

export default SkillsCardSlider;
